import { Component, HostBinding, Input, OnChanges } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock, paletteColors } from '../report-aggregation';
import type { BlockPatch } from '../report-design-edit';
import { SummaryBox, textOn } from './summary-boxes.component';

/** One stored box. Mirrors the inline item type on HeadlineStatsExtraData.boxes. */
interface BoxData {
  value: string;
  label: string;
}

/**
 * What each slot is called until someone renames it. Defaults by position, not a promise about the
 * content: `boxes` carries a label per entry and any of them can be changed to anything, so nothing
 * downstream may infer what a box holds from where it sits.
 */
const DEFAULT_LABELS = ['Aided awareness', 'Advocacy score', 'Top driver', 'Momentum'] as const;

/** Fixed at four, matching the methodology module's grid. A fifth entry is not drawn. */
const BOX_COUNT = DEFAULT_LABELS.length;

@Component({
  selector: 'app-executive-summary-block',
  standalone: false,
  template: `
    <app-summary-boxes
      [boxes]="boxes"
      [editing]="editing"
      (valueChange)="setBox($event.field, 'value', $event.value)"
      (labelChange)="setBox($event.field, 'label', $event.value)">
    </app-summary-boxes>

    @if (editing || visibleTakeaways.length) {
      <div class="takeaways mt-3">
        @if (editing) {
          <!-- Tracked by index, not by the text: tracking by value would rebuild the input on every
               keystroke and lose the caret. -->
          @for (line of takeaways; track $index; let i = $index) {
            <div class="takeaway d-flex mb-3">
              <span class="takeaway-bar" [style.background-color]="barColour"></span>
              <input type="text"
                     class="takeaway-input nav-font14 flex-fill"
                     maxlength="200"
                     placeholder="A one-line takeaway"
                     [attr.aria-label]="'Takeaway ' + (i + 1)"
                     [value]="line"
                     (input)="setLine(i, $any($event.target).value)">
              <button type="button" class="takeaway-remove btn btn-sm p-0 ml-2"
                      [attr.aria-label]="'Remove takeaway ' + (i + 1)"
                      (click)="removeLine(i)">
                <i class="fas fa-close" aria-hidden="true"></i>
              </button>
            </div>
          }
          <button type="button" class="add-takeaway btn btn-sm p-0 nav-font12" (click)="addLine()">
            <i class="fas fa-plus mr-1" aria-hidden="true"></i>Add takeaway
          </button>
        } @else {
          @for (line of visibleTakeaways; track $index; let i = $index) {
            <div class="takeaway d-flex mb-3">
              <span class="takeaway-bar" [style.background-color]="barColour"></span>
              <p class="takeaway-text nav-font14 mb-0">{{ line }}</p>
            </div>
          }
        }
      </div>
    }
  `,
  styles: [`
    :host { display: block; }
    /* stretch, not centre, so the bar grows with a takeaway that wraps to two lines. */
    .takeaway {
      align-items: stretch;
    }
    .takeaway-bar {
      flex: 0 0 14px;
      margin-right: 1rem;
      /* A floor, so a one-line takeaway still gets a bar with presence. It stretches past this
         whenever the text wraps, since the row is align-items: stretch. */
      min-height: 2rem;
    }
    /* Centred against a bar that is now taller than a single line of text. */
    .takeaway-text {
      align-self: center;
    }
    .takeaway-input {
      background: transparent;
      border: 1px solid var(--nav-grey1);
      border-radius: 3px;
      color: inherit;
      padding: 2px 4px;
    }
    .takeaway-input:hover {
      border-color: var(--nav-grey2);
    }
    .takeaway-input:focus {
      background: var(--nav-true-white);
      border-color: var(--nav-grey3);
      outline: none;
    }
    .takeaway-remove {
      color: var(--nav-grey4);
      background-color: transparent;
      border: 0;
      line-height: 1;
      width: 1.5rem;
    }
    .takeaway-remove:hover {
      color: var(--nav-error);
    }
    /* Navigator's navy, as every control that builds the report is: it is the tool, not the
       client's deck, and does not change colour with its palette. */
    .add-takeaway {
      color: var(--nav-secondary);
      background-color: transparent;
      border: 0;
    }
    .add-takeaway:hover,
    .add-takeaway:focus-visible {
      color: var(--nav-secondary);
      text-decoration: underline;
    }
  `]
})
export class ExecutiveSummaryBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;
  @Input() editing?: boolean;
  @Input() onPatch?: (patch: BlockPatch) => void;

  /**
   * Only across a whole row, matching the methodology module. At half width the boxes are already
   * narrow. Optional chaining because a host binding is evaluated before the first ngOnChanges.
   */
  @HostBinding('class.pl-5')
  @HostBinding('class.pr-5')
  get fullWidth(): boolean {
    return this.block?.segmentLocation === 'full';
  }

  boxes: SummaryBox[] = [];
  /** What is stored, including any blank line someone is part-way through typing. */
  takeaways: string[] = [];
  /** What is rendered when not editing -- a blank line would otherwise draw a bar and no text. */
  visibleTakeaways: string[] = [];

  private roles: string[] = [];
  /** The stored boxes, normalised to BOX_COUNT entries so an edit can rebuild the whole array. */
  private stored: BoxData[] = [];

  ngOnChanges() {
    if (this.block.reportType !== 'headline-stats') return;
    const data = this.block.reportExtraData;

    // Normalised on the way in rather than trusted: the array is positional and hand-editable, so a
    // short one -- or none at all -- must still draw four boxes rather than a ragged row.
    const supplied = data.boxes ?? [];
    this.stored = Array.from({ length: BOX_COUNT }, (_, i) => ({
      value: supplied[i]?.value ?? '',
      label: supplied[i]?.label ?? '',
    }));

    // Nothing here is computed: these are conclusions, not counts. Every box has an empty
    // `computed`, so an unfilled one shows an em dash. `field` is the index, since a box is
    // identified by where it sits and nothing else.
    this.roles = paletteColors(this.palette);
    this.boxes = this.stored.map((box, i) => ({
      field: String(i),
      labelField: String(i),
      label: box.label.trim() || DEFAULT_LABELS[i],
      override: box.value,
      computed: '',
      background: this.roles[i % this.roles.length],
      color: textOn(this.roles[i % this.roles.length]),
    }));

    this.takeaways = [...(data.takeaways ?? [])];
    this.visibleTakeaways = this.takeaways.map(line => line.trim()).filter(Boolean);
  }

  /**
   * Writes the whole array back, never one entry: the boxes are positional, so patching a single
   * index would leave the document holding a sparse array and renumber everything after it.
   */
  setBox(field: string, key: keyof BoxData, text: string) {
    const index = Number(field);
    if (!Number.isInteger(index) || index < 0 || index >= BOX_COUNT) return;
    this.onPatch?.({
      boxes: this.stored.map((box, i) => (i === index ? { ...box, [key]: text } : { ...box })),
    });
  }

  /**
   * One colour for every bar rather than a cycle: the boxes above already run through the palette,
   * and repeating that here made the takeaways read as if each were tied to the box sharing its
   * colour.
   *
   * The positive role, matching the page-level key-takeaway callout, so both of the deck's
   * "here is the point" surfaces speak in one colour. It was the fifth role -- picked because
   * nothing else drew with it -- which meant every takeaway on every deck was painted in the
   * colour reserved for detractors and falling numbers.
   */
  get barColour(): string {
    return this.palette.positive ?? this.palette.primary;
  }

  setLine(index: number, value: string) {
    this.emit(this.takeaways.map((line, i) => i === index ? value : line));
  }

  addLine() {
    this.emit([...this.takeaways, '']);
  }

  removeLine(index: number) {
    this.emit(this.takeaways.filter((_, i) => i !== index));
  }

  private emit(takeaways: string[]) {
    this.onPatch?.({ takeaways });
  }
}
